/**
 * Full SSR (treatment) entry for the home route. Mounted via
 * `dynamicWithExperiment` from `routes/_app._index.tsx` whenever the
 * global `SSR_DUAL_MODE_EXPERIMENT_NAME` experiment resolves to
 * `treatment`.
 *
 * Behaviour mirrors what the route's `default` Component used to do
 * inline before the dual-mode split:
 * - Build a route-scoped Apollo client seeded with the SSR-prefetched
 *   `ListArtworks` / `GetTag` payloads so `useQuery` hits cache on
 *   first render.
 * - Prefill react-query's infinite cache for the recommend feed so
 *   `useOrpcRecommendArtworks` doesn't fire a duplicate
 *   `/v2/artwork/recommend-visitor` request after hydration.
 * - Pass `ssrInitialConnectionData` down to `HomePageWrapper` so the
 *   first paint uses real artworks instead of skeletons.
 *
 * Control (CSR) entry lives in `_app._index.csr.tsx` and ignores the
 * loader's seed payloads entirely.
 */
import { ApolloProvider } from '@apollo/client/react'
import type { InferContractRouterOutputs } from '@orpc/contract'
import { QueryClientProvider } from '@tanstack/react-query'
import type { baseContract } from 'pixai-contract/api/orpc'
import { useMemo, useState } from 'react'
import { useLoaderData } from 'react-router'
import { Component as HomePageWrapper } from 'src/pages/AppPage/HomePageWrapper'
import type { loader } from 'src/routes/_app._index'
import {
  GetTagDocument,
  ListArtworksDocument,
} from 'src/services/apollo/generated/graphql'
import { createRouteApolloClient } from 'src/services/apollo/route-ssr-client'
import {
  buildRecommendArtworksInfiniteOptions,
  buildRecommendArtworksVisitorInfiniteOptions,
} from 'src/services/orpc/recommendArtwork'
import { useRequestContext } from 'src/utils/request-context'

// The home loader fetches via the `@hey-api/openapi-ts` codegen SDK
// (`ArtworkGetRecommendArtworks*Response`), whose generated TS shape
// can drift slightly from the contract-driven type that
// `@orpc/tanstack-query` infers for the hook's cache slot
// (notably: the SDK is missing `data[].contest` because the OpenAPI
// regen hasn't picked it up yet). The runtime JSON is identical, so
// the seeded payload is structurally fine for the hook to consume —
// we just need to cast across the type gap when we `setQueryData`.
type ContractRecommendPage = InferContractRouterOutputs<
  typeof baseContract
>['artwork']['getRecommendArtworks']
type ContractRecommendVisitorPage = InferContractRouterOutputs<
  typeof baseContract
>['artwork']['getRecommendArtworksVisitor']

export default function HomePageFullSSREntry() {
  const loaderData = useLoaderData<typeof loader>()
  const { queryClient } = useRequestContext()

  const client = useMemo(
    () =>
      createRouteApolloClient([
        ...(loaderData.homeApolloRequest.mode === 'list' &&
        loaderData.homeApolloRequest.feedConnection
          ? [
              {
                query: ListArtworksDocument,
                variables: loaderData.homeApolloRequest.variables,
                data: {
                  artworks: loaderData.homeApolloRequest.feedConnection,
                },
              },
            ]
          : []),
        ...(loaderData.homeApolloRequest.tagName &&
        loaderData.homeApolloRequest.tagData
          ? [
              {
                query: GetTagDocument,
                variables: { name: loaderData.homeApolloRequest.tagName },
                data: {
                  tag: loaderData.homeApolloRequest.tagData,
                },
              },
            ]
          : []),
      ]),
    [loaderData.homeApolloRequest],
  )

  const recommendRawResponse =
    loaderData.homeApolloRequest.mode === 'recommend'
      ? loaderData.homeApolloRequest.feedRawResponse
      : null
  const recommendFirst =
    loaderData.homeApolloRequest.mode === 'recommend'
      ? (loaderData.homeApolloRequest.variables.first ?? 24)
      : null
  // Pick the cache key that matches the endpoint the home loader
  // actually hit. The loader forwards the SSR cookie to either
  // `/v2/artwork/recommend` (logged-in) or `/v2/artwork/recommend-visitor`
  // (anonymous); `useOrpcRecommendArtworks` does the same split on
  // the client via `useMayTokenBeValid()`. Prefilling under the
  // matching key means the client's first hydrate render sees the
  // raw response sitting in cache and skips the duplicate fetch +
  // grid-swap that used to fire when the loader unconditionally
  // wrote into the visitor key but the user was actually logged in.
  //
  // The hook talks to the *contract-driven* `orpc` utils
  // (`@orpc/tanstack-query`), so its key shape is
  // `[['artwork', 'getRecommendArtworks(Visitor)'], { input, type: 'infinite' }]`
  // — completely different from the `@hey-api/openapi-ts` codegen
  // `artworkGetRecommendArtworks*InfiniteQueryKey({ client, query })`
  // that we used to write into here. Going through the shared
  // `buildRecommendArtworks*InfiniteOptions(first)` builders that
  // the hook itself spreads into `useInfiniteQuery` guarantees the
  // two sides land on the exact same cache slot.
  const ssrIsLoggedIn = loaderData.homeApolloRequest.ssrIsLoggedIn === true

  useState(() => {
    if (!recommendRawResponse || recommendFirst == null) return null
    if (ssrIsLoggedIn) {
      const options = buildRecommendArtworksInfiniteOptions(recommendFirst)
      if (queryClient.getQueryData(options.queryKey)) return null
      queryClient.setQueryData(options.queryKey, {
        pages: [recommendRawResponse as ContractRecommendPage],
        // `initialPageParam: undefined` in the hook means the first
        // page was fetched with no cursor, so the seeded entry must
        // mirror that or `fetchNextPage()` will mis-build its key when
        // the user scrolls.
        pageParams: [undefined],
      })
    } else {
      const options =
        buildRecommendArtworksVisitorInfiniteOptions(recommendFirst)
      if (queryClient.getQueryData(options.queryKey)) return null
      queryClient.setQueryData(options.queryKey, {
        pages: [recommendRawResponse as ContractRecommendVisitorPage],
        pageParams: [undefined],
      })
    }
    return null
  })

  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={client}>
        <HomePageWrapper
          ssrInitialConnectionData={
            loaderData.homePreview.initialConnectionData
          }
        />
      </ApolloProvider>
    </QueryClientProvider>
  )
}
